import { map, pick, merge, path, nthArg, prop, identity, compose, sum, filter, propOr, isNil, concat, defaultTo, isEmpty } from 'ramda';
import { createAction } from 'redux-actions';
import { fetchGamblingHistory, fetchGameTransactionsTotals } from '@rushplay/api-client';
import { start, stop, isRunning } from '@rushplay/processes';
import { createSelector, createStructuredSelector } from 'reselect';
import { SESSION_EXPIRED } from '@rushplay/websockets';
import { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Path to nested property represented as array of property names
 *
 * @typedef {Array.<string>} PropertyPath
 *
 * @example // Path to "c" in "object"
 * const object = {
 *   a: {
 *     b: {
 *       c: ''
 *     }
 *   }
 * }
 *
 * const path = ['a', 'b', 'c']
 */

/**
 * @typedef {Object.<string, PropertyPath>} MountPoints
 * @property {PropertyPath} "@rushplay/compliance/gambling-history"
 *   Path to `@rushplay/compliance/gambling-history` reducer’s state
 * @property {PropertyPath} "@rushplay/processes"
 *   Path to `@rushplay/processes` reducer’s state
 */

var CLEARED = 'compliance/gambling-history/CLEARED';
var PAGE_APPENDED = 'compliance/gambling-history/PAGE_APPENDED';
var TOTALS_UPDATED = 'compliance/gambling-history/TOTALS_UPDATED';

var FIRST_CREATED_GAME_TRANSACTION = 1422748801103;
var TOTALS_LOADING = '@rushplay/compliance/TOTALS_LOADING';
var TRANSACTIONS_LOADING = '@rushplay/compliance/TRANSACTIONS_LOADING';

function noop() {}
/**
 * Cursor contains a timestamp; which is
 * used to retrive more history chunks
 * @typedef {Object} cursor
 * @property {number} timestamp Timestamp
 */

/**
 * Fetcher for gambling history
 * @param {cursor} cursor Cursor
 * @param {number} limit Number of transactions fetched per load
 * @param {function} onError A function do dispatch on failure
 * @param {number|null} startDateTimestamp Timestamp of history start date
 * @param {number|null} endDateTimestamp Timestamp of history end date
 * @param {boolean} excludeCasino Exclude casino transactions
 * @param {boolean} excludeSport Exclude sport transactions
 * @returns {Object} Redux Action
 */


function fetch(cursor, limit) {
  var onError = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  var startDateTimestamp = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  var endDateTimestamp = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : null;
  var excludeCasino = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
  var excludeSport = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  return [start(TRANSACTIONS_LOADING), fetchGamblingHistory(limit, {
    cursor: cursor,
    startDate: startDateTimestamp,
    endDate: endDateTimestamp,
    excludeCasino: excludeCasino,
    excludeSport: excludeSport
  }, {
    success: function success(res) {
      var result = map(pick(['bonusAmountCents', 'bonusType', 'createdAt', 'currency', 'gameTitle', 'moneyAmountCents', 'provider', 'type', 'withdrawAmountCents', 'subProvider', 'externalReason']), res.value.result);
      var payload = merge(pick(['cursor'], res.value), {
        result: result
      });
      return [save(payload), stop(TRANSACTIONS_LOADING)];
    },
    failure: function failure(res) {
      return [stop(TRANSACTIONS_LOADING), onError(res)];
    },
    version: 2
  })];
}
function fetchTotals() {
  var onError = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : noop;
  return [start(TOTALS_LOADING), fetchGameTransactionsTotals({
    success: function success(res) {
      return [stop(TOTALS_LOADING), updateTotals(res.value.totals)];
    },
    failure: function failure(res) {
      return [stop(TOTALS_LOADING), onError(res)];
    },
    version: 2
  })];
}
/**
 * Clears gambling transactions from state
 * @returns {Object} Redux action
 */

var clear = createAction(CLEARED);
/**
 * Stores gambling transactions to state
 * @param {Array} history Gambling History
 * @returns {Object} Redux action
 */

var save = createAction(PAGE_APPENDED);
/**
 * Stores bet totals
 * @param {Object} totals Bet totals
 * @returns {Object} Redux action
 */

var updateTotals = createAction(TOTALS_UPDATED);

function mapSelectorToState(mountPath, selector) {
  return createSelector([path(mountPath), nthArg(1)], selector);
}
function createGamblingHistoryStateSelector(_ref) {
  var mountPoints = _ref.mountPoints;

  function getMountPath() {
    if (mountPoints != null) {
      return prop('@rushplay/compliance/gambling-history', mountPoints);
    }

    return null;
  }

  return createSelector([getMountPath, identity], function (path$$1, state) {
    if (path$$1 == null) {
      return null;
    }

    return path(path$$1, state);
  });
}
function createProcessesStateSelector() {
  function getMountPath(state, props) {
    if (props.mountPoints != null) {
      return prop('@rushplay/processes', props.mountPoints);
    }

    return null;
  }

  return createSelector([getMountPath, identity], function (path$$1, state) {
    if (path$$1 == null) {
      return null;
    }

    return path(path$$1, state);
  });
}
/**
 * Computes bet and win of item
 * @param {Object} item
 * @returns {Object} Contains transaction items bet and win
 */

function getTransactionValues(item) {
  var total = compose(Math.abs, sum, filter);
  return {
    bet: total(function (n) {
      return n < 0;
    }, [item.bonusAmountCents, item.moneyAmountCents]),
    win: total(function (n) {
      return n > 0;
    }, [item.bonusAmountCents, item.moneyAmountCents])
  };
}
function getTransactionType(item) {
  var realMoney = item.moneyAmountCents !== 0 && item.bonusAmountCents === 0;
  var mixedBet = item.moneyAmountCents !== 0 && item.bonusAmountCents !== 0;
  var freeSpins = item.bonusType === 'freespins' && item.provider !== 'Internal';
  var featureTriggers = item.bonusType === 'feature_triggers';
  var speedyStart = item.moneyAmountCents === 0 && item.bonusAmountCents !== 0 && item.bonusType === 'feature_triggers' || item.type === 'purchase_transaction';
  var bonusMoney = item.moneyAmountCents === 0 && item.bonusAmountCents !== 0 && (item.type === 'bet_transaction' || item.type === 'bonus_transaction' || item.type === 'manual_adjustment_transaction') || item.type === 'settle_transaction';
  var unknown = item.moneyAmountCents === 0 && item.bonusAmountCents === 0 && item.type === 'settle_transaction';

  if (unknown) {
    return 'unknown';
  } else if (speedyStart) {
    return 'speedy-start';
  } else if (featureTriggers) {
    return 'feature-triggers';
  } else if (freeSpins) {
    return 'freespins';
  } else if (realMoney) {
    return 'real-money';
  } else if (mixedBet) {
    return 'mixed';
  } else if (bonusMoney) {
    return 'bonus-money';
  } else {
    return 'real-money';
  }
}
/**
 * @typedef {Object} transactionsItem
 * @property {number} createdAt Item created at
 * @property {String} gameTitle Game title
 * @property {number} bet Bet
 * @property {number} win Win
 * @property {String} transactionType Transaction type
 */

/**
 * @typedef {Array} transactions
 * @returns {Array.<transactionsItem>} Returns an array of transaction item(s)
 */

/**
 * Returns array of transaction items
 * @returns {transactions}
 */

function getTransactions(state) {
  return map(function (item) {
    return merge(item, {
      type: getTransactionType(item),
      values: getTransactionValues(item)
    });
  }, propOr([], 'transactions', state));
}
function areTransactionsStale(state) {
  return isNil(prop('transactions', state));
}
/**
 * @typedef {Object} cursor
 * @property {number} timestamp Timestamp is used to retrive next chunk of history
 */

/**
 * Get cursor if present; empty object otherwise
 * @returns {cursor}
 */

function getCursor(state) {
  return path(['cursor'], state);
}
/**
 * @typedef {Object} Totals
 * @property {number} betAmountCents Total bet amount in cents
 * @property {number} winAmountCents Total win amount in cents
 * @property {string} currency Currency
 */

/**
 * Gets total of bet and win of history
 * @param {Object} history History state
 * @returns {Totals} totals Total bet and win
 */

function getTotals(state) {
  return propOr({}, 'totals', state);
}
function areTotalsStale(state) {
  return isNil(prop('totals', state));
}

function symbolObservablePonyfill(root) {
	var result;
	var Symbol = root.Symbol;

	if (typeof Symbol === 'function') {
		if (Symbol.observable) {
			result = Symbol.observable;
		} else {
			result = Symbol('observable');
			Symbol.observable = result;
		}
	} else {
		result = '@@observable';
	}

	return result;
}

/* global window */

var root;

if (typeof self !== 'undefined') {
  root = self;
} else if (typeof window !== 'undefined') {
  root = window;
} else if (typeof global !== 'undefined') {
  root = global;
} else if (typeof module !== 'undefined') {
  root = module;
} else {
  root = Function('return this')();
}

var result = symbolObservablePonyfill(root);

/**
 * These are private action types reserved by Redux.
 * For any unknown actions, you must return the current state.
 * If the current state is undefined, you must return the initial state.
 * Do not reference these action types directly in your code.
 */
var randomString = function randomString() {
  return Math.random().toString(36).substring(7).split('').join('.');
};

var ActionTypes = {
  INIT: "@@redux/INIT" + randomString(),
  REPLACE: "@@redux/REPLACE" + randomString(),
  PROBE_UNKNOWN_ACTION: function PROBE_UNKNOWN_ACTION() {
    return "@@redux/PROBE_UNKNOWN_ACTION" + randomString();
  }
};

/**
 * @param {any} obj The object to inspect.
 * @returns {boolean} True if the argument appears to be a plain object.
 */
function isPlainObject(obj) {
  if (typeof obj !== 'object' || obj === null) return false;
  var proto = obj;

  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }

  return Object.getPrototypeOf(obj) === proto;
}

/**
 * Prints a warning in the console if it exists.
 *
 * @param {String} message The warning message.
 * @returns {void}
 */
function warning(message) {
  /* eslint-disable no-console */
  if (typeof console !== 'undefined' && typeof console.error === 'function') {
    console.error(message);
  }
  /* eslint-enable no-console */


  try {
    // This error was thrown as a convenience so that if you enable
    // "break on all exceptions" in your console,
    // it would pause the execution at this line.
    throw new Error(message);
  } catch (e) {} // eslint-disable-line no-empty

}

function getUndefinedStateErrorMessage(key, action) {
  var actionType = action && action.type;
  var actionDescription = actionType && "action \"" + String(actionType) + "\"" || 'an action';
  return "Given " + actionDescription + ", reducer \"" + key + "\" returned undefined. " + "To ignore an action, you must explicitly return the previous state. " + "If you want this reducer to hold no value, you can return null instead of undefined.";
}

function getUnexpectedStateShapeWarningMessage(inputState, reducers, action, unexpectedKeyCache) {
  var reducerKeys = Object.keys(reducers);
  var argumentName = action && action.type === ActionTypes.INIT ? 'preloadedState argument passed to createStore' : 'previous state received by the reducer';

  if (reducerKeys.length === 0) {
    return 'Store does not have a valid reducer. Make sure the argument passed ' + 'to combineReducers is an object whose values are reducers.';
  }

  if (!isPlainObject(inputState)) {
    return "The " + argumentName + " has unexpected type of \"" + {}.toString.call(inputState).match(/\s([a-z|A-Z]+)/)[1] + "\". Expected argument to be an object with the following " + ("keys: \"" + reducerKeys.join('", "') + "\"");
  }

  var unexpectedKeys = Object.keys(inputState).filter(function (key) {
    return !reducers.hasOwnProperty(key) && !unexpectedKeyCache[key];
  });
  unexpectedKeys.forEach(function (key) {
    unexpectedKeyCache[key] = true;
  });
  if (action && action.type === ActionTypes.REPLACE) return;

  if (unexpectedKeys.length > 0) {
    return "Unexpected " + (unexpectedKeys.length > 1 ? 'keys' : 'key') + " " + ("\"" + unexpectedKeys.join('", "') + "\" found in " + argumentName + ". ") + "Expected to find one of the known reducer keys instead: " + ("\"" + reducerKeys.join('", "') + "\". Unexpected keys will be ignored.");
  }
}

function assertReducerShape(reducers) {
  Object.keys(reducers).forEach(function (key) {
    var reducer = reducers[key];
    var initialState = reducer(undefined, {
      type: ActionTypes.INIT
    });

    if (typeof initialState === 'undefined') {
      throw new Error("Reducer \"" + key + "\" returned undefined during initialization. " + "If the state passed to the reducer is undefined, you must " + "explicitly return the initial state. The initial state may " + "not be undefined. If you don't want to set a value for this reducer, " + "you can use null instead of undefined.");
    }

    if (typeof reducer(undefined, {
      type: ActionTypes.PROBE_UNKNOWN_ACTION()
    }) === 'undefined') {
      throw new Error("Reducer \"" + key + "\" returned undefined when probed with a random type. " + ("Don't try to handle " + ActionTypes.INIT + " or other actions in \"redux/*\" ") + "namespace. They are considered private. Instead, you must return the " + "current state for any unknown actions, unless it is undefined, " + "in which case you must return the initial state, regardless of the " + "action type. The initial state may not be undefined, but can be null.");
    }
  });
}
/**
 * Turns an object whose values are different reducer functions, into a single
 * reducer function. It will call every child reducer, and gather their results
 * into a single state object, whose keys correspond to the keys of the passed
 * reducer functions.
 *
 * @param {Object} reducers An object whose values correspond to different
 * reducer functions that need to be combined into one. One handy way to obtain
 * it is to use ES6 `import * as reducers` syntax. The reducers may never return
 * undefined for any action. Instead, they should return their initial state
 * if the state passed to them was undefined, and the current state for any
 * unrecognized action.
 *
 * @returns {Function} A reducer function that invokes every reducer inside the
 * passed object, and builds a state object with the same shape.
 */


function combineReducers(reducers) {
  var reducerKeys = Object.keys(reducers);
  var finalReducers = {};

  for (var i = 0; i < reducerKeys.length; i++) {
    var key = reducerKeys[i];

    if (process.env.NODE_ENV !== 'production') {
      if (typeof reducers[key] === 'undefined') {
        warning("No reducer provided for key \"" + key + "\"");
      }
    }

    if (typeof reducers[key] === 'function') {
      finalReducers[key] = reducers[key];
    }
  }

  var finalReducerKeys = Object.keys(finalReducers);
  var unexpectedKeyCache;

  if (process.env.NODE_ENV !== 'production') {
    unexpectedKeyCache = {};
  }

  var shapeAssertionError;

  try {
    assertReducerShape(finalReducers);
  } catch (e) {
    shapeAssertionError = e;
  }

  return function combination(state, action) {
    if (state === void 0) {
      state = {};
    }

    if (shapeAssertionError) {
      throw shapeAssertionError;
    }

    if (process.env.NODE_ENV !== 'production') {
      var warningMessage = getUnexpectedStateShapeWarningMessage(state, finalReducers, action, unexpectedKeyCache);

      if (warningMessage) {
        warning(warningMessage);
      }
    }

    var hasChanged = false;
    var nextState = {};

    for (var _i = 0; _i < finalReducerKeys.length; _i++) {
      var _key = finalReducerKeys[_i];
      var reducer = finalReducers[_key];
      var previousStateForKey = state[_key];
      var nextStateForKey = reducer(previousStateForKey, action);

      if (typeof nextStateForKey === 'undefined') {
        var errorMessage = getUndefinedStateErrorMessage(_key, action);
        throw new Error(errorMessage);
      }

      nextState[_key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }

    return hasChanged ? nextState : state;
  };
}

/*
 * This is a dummy function to check if the function name has been altered by minification.
 * If the function has been minified and NODE_ENV !== 'production', warn the user.
 */

function isCrushed() {}

if (process.env.NODE_ENV !== 'production' && typeof isCrushed.name === 'string' && isCrushed.name !== 'isCrushed') {
  warning('You are currently using minified code outside of NODE_ENV === "production". ' + 'This means that you are running a slower development build of Redux. ' + 'You can use loose-envify (https://github.com/zertosh/loose-envify) for browserify ' + 'or setting mode to production in webpack (https://webpack.js.org/concepts/mode/) ' + 'to ensure you have the correct code for your production build.');
}

function transactions() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (path(['error'], action)) {
    return state;
  }

  switch (path(['type'], action)) {
    case PAGE_APPENDED:
      {
        return concat(defaultTo([], state), action.payload.result);
      }

    case SESSION_EXPIRED:
    case CLEARED:
      {
        return null;
      }

    default:
      {
        return state;
      }
  }
}
function totals() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (path(['error'], action)) {
    return state;
  }

  switch (path(['type'], action)) {
    case TOTALS_UPDATED:
      {
        return action.payload;
      }

    case SESSION_EXPIRED:
    case CLEARED:
      {
        return null;
      }

    default:
      {
        return state;
      }
  }
}
function cursor() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (path(['error'], action)) {
    return state;
  }

  switch (path(['type'], action)) {
    case PAGE_APPENDED:
      {
        return isEmpty(action.payload.result) ? {} : path(['payload', 'cursor'], action);
      }

    case SESSION_EXPIRED:
    case CLEARED:
      {
        return {};
      }

    default:
      {
        return state;
      }
  }
}
var reducer = combineReducers({
  cursor: cursor,
  totals: totals,
  transactions: transactions
});

/**
 * Cursor contains a timestamp; which is
 * used to retrive more history chunks
 * @typedef {Object} cursor
 * @property {number} timestamp Timestamp
 */

function createMapStateToProps(_ref) {
  var mountPoints = _ref.mountPoints;
  var TRANSACTIONS_LOADING_PROPS = {
    ids: [TRANSACTIONS_LOADING]
  };
  var getCursor$$1 = mapSelectorToState(mountPoints['@rushplay/compliance/gambling-history'], getCursor);
  var getLoading = mapSelectorToState(mountPoints['@rushplay/processes'], function (state) {
    return isRunning(state, TRANSACTIONS_LOADING_PROPS);
  });
  var getTransactions$$1 = mapSelectorToState(mountPoints['@rushplay/compliance/gambling-history'], getTransactions);
  var areTransactionsStale$$1 = mapSelectorToState(mountPoints['@rushplay/compliance/gambling-history'], areTransactionsStale);
  return createStructuredSelector({
    cursor: getCursor$$1,
    data: getTransactions$$1,
    loading: getLoading,
    stale: areTransactionsStale$$1
  });
}
/**
 *
 * @param {Object} props
 * @param {MountPoints} props.mountPoints
 * @param {Function} props.onError
 * @param {Number} props.startDate
 * @param {Number} props.endDate
 */


function useTransactions(_ref2) {
  var mountPoints = _ref2.mountPoints,
      onError = _ref2.onError,
      _ref2$startDate = _ref2.startDate,
      startDate = _ref2$startDate === void 0 ? null : _ref2$startDate,
      _ref2$endDate = _ref2.endDate,
      endDate = _ref2$endDate === void 0 ? null : _ref2$endDate;
  var dispatch = useDispatch();
  var mapStateToProps = useMemo(function () {
    return createMapStateToProps({
      mountPoints: mountPoints
    });
  }, [mountPoints]);

  var _ReactRedux$useSelect = useSelector(mapStateToProps),
      cursor = _ReactRedux$useSelect.cursor,
      data = _ReactRedux$useSelect.data,
      loading = _ReactRedux$useSelect.loading,
      stale = _ReactRedux$useSelect.stale;

  var empty = useMemo(function () {
    return !loading && !stale && data.length === 0;
  }, [data, loading, stale]);
  var onClear = useCallback(function () {
    return dispatch(clear());
  }, [dispatch]);
  var onFetch = useCallback(function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return dispatch(fetch(null, null, onError, startDate, endDate, options.excludeCasino, options.excludeSport));
  }, [dispatch, onError, startDate, endDate]);
  var onFetchMore = useMemo(function () {
    if (cursor.timestamp == null) {
      return;
    }

    return function () {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return dispatch(fetch(cursor, null, onError, startDate, endDate, options.excludeCasino, options.excludeSport));
    };
  }, [cursor, dispatch, onError, startDate, endDate]);
  return useMemo(function () {
    return {
      data: data,
      empty: empty,
      loading: loading,
      stale: stale,
      onClear: onClear,
      onFetch: onFetch,
      onFetchMore: onFetchMore
    };
  }, [data, empty, loading, stale, onClear, onFetch, onFetchMore]);
}

function createMapStateToProps$1(_ref) {
  var mountPoints = _ref.mountPoints;
  var TOTALS_LOADING_PROPS = {
    ids: [TOTALS_LOADING]
  };
  var getLoading = mapSelectorToState(mountPoints['@rushplay/processes'], function (state) {
    return isRunning(state, TOTALS_LOADING_PROPS);
  });
  var getTotals$$1 = mapSelectorToState(mountPoints['@rushplay/compliance/gambling-history'], getTotals);
  var areTotalsStale$$1 = mapSelectorToState(mountPoints['@rushplay/compliance/gambling-history'], areTotalsStale);
  return createStructuredSelector({
    data: getTotals$$1,
    loading: getLoading,
    stale: areTotalsStale$$1
  });
}

function useTotals(_ref2) {
  var mountPoints = _ref2.mountPoints,
      onError = _ref2.onError;
  var dispatch = useDispatch();
  var mapStateToProps = useMemo(function () {
    return createMapStateToProps$1({
      mountPoints: mountPoints
    });
  }, [mountPoints]);

  var _ReactRedux$useSelect = useSelector(mapStateToProps),
      data = _ReactRedux$useSelect.data,
      loading = _ReactRedux$useSelect.loading,
      stale = _ReactRedux$useSelect.stale;

  var empty = useMemo(function () {
    return !loading && !stale && data.betAmountCents == null;
  }, [data, loading, stale]);
  var onFetch = useCallback(function () {
    return dispatch(fetchTotals(onError));
  }, [dispatch, onError]);
  return useMemo(function () {
    return {
      data: data,
      empty: empty,
      loading: loading,
      stale: stale,
      onFetch: onFetch
    };
  }, [data, empty, loading, stale, onFetch]);
}

export { useTransactions, useTotals, fetch, fetchTotals, clear, save, updateTotals, FIRST_CREATED_GAME_TRANSACTION, TOTALS_LOADING, TRANSACTIONS_LOADING, mapSelectorToState, createGamblingHistoryStateSelector, createProcessesStateSelector, getTransactionValues, getTransactionType, getTransactions, areTransactionsStale, getCursor, getTotals, areTotalsStale, CLEARED, PAGE_APPENDED, TOTALS_UPDATED, transactions, totals, cursor, reducer };
